import type { PostHog } from "posthog-js";

import {
  APPROVED_EVENTS,
  APP_VERSION,
  durationBucket,
  findUnsafeProperty,
  normalizeRoute,
  sanitizeProperties,
  scrubDefaultProperties,
  type AnalyticsEvent,
  type AnalyticsProperties,
} from "./events";

export {
  APPROVED_EVENTS,
  APPROVED_PROPERTIES,
  APP_VERSION,
  durationBucket,
  normalizeRoute,
  type AnalyticsEvent,
  type AnalyticsProperties,
} from "./events";

const EVENT_ALLOWLIST = new Set<string>(APPROVED_EVENTS);
const ANON_ID_KEY = "looseend.analytics.anon_id.v1";
const OPT_OUT_KEY = "looseend.analytics.disabled.v1";
/** Identical event+property signatures inside this window are dropped. */
const DEDUPE_WINDOW_MS = 1_500;

let client: PostHog | null = null;
let initializing: Promise<PostHog | null> | null = null;
let enabled = true;
const recentEvents = new Map<string, number>();

/** Analytics must never surface an error into the product. */
function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

function isBrowser() {
  return typeof window !== "undefined";
}

function environment(): string {
  if (!isBrowser()) return "server";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "local";
  if (host.includes("id-preview") || host.endsWith("-dev.lovable.app")) return "preview";
  return "production";
}

function deviceCategory(): string {
  if (!isBrowser()) return "unknown";
  const width = window.innerWidth;
  if (width < 640) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

/** Anonymous, device-local identifier — never an email or user-entered value. */
function anonymousId(): string {
  if (!isBrowser()) return "anonymous";
  const existing = safe(() => window.localStorage.getItem(ANON_ID_KEY));
  if (existing) return existing;
  const generated = `anon_${
    safe(() => window.crypto.randomUUID())?.replace(/-/g, "") ??
    Math.random().toString(36).slice(2) + Date.now().toString(36)
  }`;
  safe(() => window.localStorage.setItem(ANON_ID_KEY, generated));
  return generated;
}

function apiHost(): string {
  const region =
    (import.meta.env["VITE_LOVABLE_CONNECTOR_POSTHOG_REGION"] as string | undefined) || "eu";
  return region === "us" ? "https://us.i.posthog.com" : "https://eu.i.posthog.com";
}

async function ensureClient(): Promise<PostHog | null> {
  if (!isBrowser() || !enabled) return null;
  if (client) return client;
  if (initializing) return initializing;

  const token = import.meta.env["VITE_LOVABLE_CONNECTOR_POSTHOG_API_KEY"] as string | undefined;
  if (!token) return null;

  initializing = (async () => {
    try {
      const { default: posthog } = await import("posthog-js");
      posthog.init(token, {
        api_host: apiHost(),
        // Product Analytics only — every passive capture surface stays off.
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        capture_performance: false,
        capture_dead_clicks: false,
        capture_heatmaps: false,
        disable_session_recording: true,
        disable_surveys: true,
        rageclick: false,
        mask_all_text: true,
        mask_all_element_attributes: true,
        // Anonymous events must never create a PostHog person profile.
        person_profiles: "identified_only",
        // GeoIP enrichment is refused per-event via `$geoip_disable: true`.
        // Raw-IP discarding stays authoritative at the PostHog project level.
        persistence: "localStorage",

        // Anonymous prototype ID only — no identify(), no person properties.
        bootstrap: { distinctID: anonymousId() },
        before_send: (event) => {
          if (!event) return null;
          // Never emit person-property events.
          if (event.event === "$set") return null;

          const scrubbed = scrubDefaultProperties(
            (event.properties ?? {}) as Record<string, unknown>,
          );
          const offending = findUnsafeProperty(scrubbed);
          if (offending) {
            if (import.meta.env.DEV) {
              console.warn(`[analytics] rejected "${event.event}": unsafe property "${offending}"`);
            }
            return null;
          }
          const {
            $set: _set,
            $set_once: _setOnce,
            ...rest
          } = event as typeof event & {
            $set?: unknown;
            $set_once?: unknown;
          };
          return { ...rest, properties: scrubbed } as typeof event;
        },
      });

      client = posthog;
      return posthog;
    } catch {
      return null;
    } finally {
      initializing = null;
    }
  })();

  return initializing;
}

function isDuplicate(signature: string): boolean {
  const now = Date.now();
  for (const [key, at] of recentEvents) {
    if (now - at > DEDUPE_WINDOW_MS) recentEvents.delete(key);
  }
  if (recentEvents.has(signature)) return true;
  recentEvents.set(signature, now);
  return false;
}

export const analytics = {
  /** Enable/disable all analytics globally at runtime. */
  setEnabled(next: boolean) {
    enabled = next;
    safe(() => {
      if (next) window.localStorage.removeItem(OPT_OUT_KEY);
      else window.localStorage.setItem(OPT_OUT_KEY, "1");
    });
    safe(() => (next ? client?.opt_in_capturing() : client?.opt_out_capturing()));
  },
  isEnabled() {
    return enabled;
  },

  init() {
    if (!isBrowser()) return;
    if (safe(() => window.localStorage.getItem(OPT_OUT_KEY)) === "1") {
      enabled = false;
      return;
    }
    void ensureClient();
  },

  /** Fire-and-forget: allowlisted name, sanitized categorical properties only. */
  track(event: AnalyticsEvent, properties?: AnalyticsProperties) {
    if (!enabled || !isBrowser()) return;
    if (!EVENT_ALLOWLIST.has(event)) return;
    // Recipient share pages never emit analytics — the route carries a token.
    if (normalizeRoute(window.location.pathname) === "/share/:token") return;

    // Reject the whole event when any *incoming* value looks unsafe, so a
    // caller mistake is surfaced instead of silently stripped.
    const raw = findUnsafeProperty((properties ?? {}) as Record<string, unknown>);
    const props = sanitizeProperties(properties);
    const offending = raw ?? findUnsafeProperty(props as Record<string, unknown>);
    if (offending) {
      if (import.meta.env.DEV) {
        console.warn(`[analytics] rejected "${event}": unsafe property "${offending}"`);
      }
      return;
    }

    if (isDuplicate(`${event}:${JSON.stringify(props)}`)) return;

    void (async () => {
      const ph = await ensureClient();
      if (!ph) return;
      safe(() =>
        ph.capture(event, {
          ...props,
          environment: environment(),
          app_version: APP_VERSION,
          device_category: props.device_category ?? deviceCategory(),
          // Central privacy controls — never set from UI components.
          $geoip_disable: true,
          $process_person_profile: false,
        }),
      );
    })();
  },

  /** Real auth only: pass the stable internal user ID — never an email. */
  identifyUser(internalUserId: string) {
    if (!enabled || !internalUserId) return;
    if (/[@\s]/.test(internalUserId)) {
      if (import.meta.env.DEV)
        console.warn("[analytics] rejected identify: value is not an internal ID");
      return;
    }
    void (async () => {
      const ph = await ensureClient();
      // No person properties, ever — identity is the internal ID alone.
      safe(() => ph?.identify(internalUserId));
    })();
  },

  reset() {
    safe(() => client?.reset());
    safe(() => window.localStorage.removeItem(ANON_ID_KEY));
    recentEvents.clear();
  },
};

export const track = analytics.track;
